import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaEnvelope, FaLock, FaArrowLeft } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success('Si el email existe, recibirás un enlace para restablecer tu contraseña');
    } catch (error) {
      toast.success('Si el email existe, recibirás un enlace');
    } finally {
      setForgotLoading(false);
    }
  };

  const getRedirectUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('redirect') || '/dashboard';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const loginResponse = await axios.post(`${API}/auth/login`, 
        { email, password },
        { withCredentials: false }
      );
      
      if (loginResponse.data.success) {
        const userData = loginResponse.data.user;
        const token = loginResponse.data.token;
        
        // Verificar si es admin
        const isAdmin = userData.role === 'admin';
        
        // IMPORTANTE: Si NO es admin, limpiar cualquier token de admin que pueda existir
        // Esto previene que usuarios normales accedan al panel admin con tokens viejos
        if (!isAdmin) {
          localStorage.removeItem('deisycfit_admin_token');
          localStorage.removeItem('deisycfit_admin_user');
          localStorage.removeItem('deisycfit_admin_session_ts');
        }
        
        // Guardar datos del usuario directamente de la respuesta del login
        localStorage.setItem('deisycfit_user', JSON.stringify({
          id: userData.id,
          email: userData.email,
          name: userData.name,
          subscription_plan: userData.subscription_plan,
          gender: userData.gender,
          role: userData.role,
          must_change_password: userData.must_change_password || false
        }));
        localStorage.setItem('deisycfit_logged_in', 'true');
        if (token) {
          localStorage.setItem('deisycfit_token', token);
        }
        
        // Si es admin, también guardar tokens de admin para el panel
        if (isAdmin) {
          localStorage.setItem('deisycfit_admin_token', token);
          localStorage.setItem('deisycfit_admin_user', JSON.stringify(userData));
          localStorage.setItem('deisycfit_admin_session_ts', Date.now().toString());
          toast.success('¡Bienvenida al panel de administración!');
          setTimeout(() => {
            window.location.href = '/admin';
          }, 500);
          return;
        }
        
        toast.success('¡Bienvenida!');
        
        const redirectUrl = getRedirectUrl();
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 500);
      }
    } catch (error) {
      toast.error('Email o contraseña incorrectos');
      console.error(error);
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-6 relative"
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 30% 20%, rgba(var(--brand-primary-glow), 0.15), transparent 50%), radial-gradient(circle at 70% 80%, rgba(0, 240, 255, 0.1), transparent 40%)'
        }}
      />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="absolute -top-16 left-0 text-gray-400 hover:text-white"
        >
          <FaArrowLeft className="mr-2" /> Volver al inicio
        </Button>

        {/* Logo */}
        <div className="text-center mb-10">
          <h1 
            className="text-4xl font-bold text-white mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            DeisyCFit
          </h1>
          <p className="text-gray-400">Inicia sesión en tu cuenta</p>
        </div>

        {/* Login Card */}
        <div 
          className="rounded-3xl p-8 backdrop-blur-xl"
          style={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <h2 
            className="text-2xl font-bold text-white mb-6"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Iniciar Sesión
          </h2>
          
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label className="text-gray-300 text-sm font-medium mb-2 block">Email</Label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@email.com"
                  required
                  className="w-full pl-11 py-6 rounded-xl bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]/20"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-gray-300 text-sm font-medium mb-2 block">Contraseña</Label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-11 py-6 rounded-xl bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)]/20"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full py-6 rounded-full font-bold text-white uppercase tracking-wider transition-all"
              style={{ 
                background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
              }}
              disabled={loading}
            >
              {loading ? 'Ingresando...' : 'Iniciar Sesión'}
            </Button>
            
            <div className="text-center">
              <button 
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-gray-400 hover:text-pink-400 transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>
        </div>

        {/* Register Link */}
        <div className="mt-8 text-center">
          <p className="text-gray-400">
            ¿No tienes cuenta?{' '}
            <button 
              onClick={() => navigate('/register')}
              className="font-semibold hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              Regístrate aquí
            </button>
          </p>
        </div>
      </motion.div>

      {/* Modal Olvidé mi contraseña */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-zinc-900 rounded-2xl p-6 w-full max-w-md"
          >
            {!forgotSent ? (
              <>
                <h3 className="text-xl font-bold text-white mb-2">Recuperar Contraseña</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
                </p>
                <form onSubmit={handleForgotPassword}>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="tu@email.com"
                    required
                    className="w-full mb-4 bg-zinc-800 border-zinc-700 text-white"
                  />
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForgotPassword(false)}
                      className="flex-1 border-zinc-700 text-gray-300"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={forgotLoading}
                      className="flex-1 text-white"
                      style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                    >
                      {forgotLoading ? 'Enviando...' : 'Enviar'}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaEnvelope className="text-2xl text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">¡Revisa tu email!</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Si el email está registrado, recibirás un enlace para restablecer tu contraseña.
                </p>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSent(false);
                    setForgotEmail('');
                  }}
                  className="w-full text-white"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                >
                  Entendido
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Login;
